// Builds and renders removable chips for active screener filters.
import { formatCurrency, formatExchangeLabel, formatNumber } from "../../lib/format";
import type { ScreenerFilterState } from "../../lib/types";
import { FilterChip } from "../ui/filter-chip";

interface ActiveFilterChipsProps {
  filters: ScreenerFilterState;
  onFilterPatch: (filters: Partial<ScreenerFilterState>) => void;
}

interface ActiveChip {
  key: string;
  label: string;
  value: string;
  removeLabel: string;
  onRemove: () => void;
}

function joined(values: readonly string[]): string {
  return values.join(", ");
}

function rangeValue(
  minimum: number | null,
  maximum: number | null,
  formatter: (value: number) => string = formatNumber
): string {
  if (minimum !== null && maximum !== null) return `${formatter(minimum)}-${formatter(maximum)}`;
  if (minimum !== null) return `${formatter(minimum)}+`;
  return `Up to ${formatter(maximum!)}`;
}

function dateRangeValue(from: string | null, to: string | null): string {
  if (from !== null && to !== null) return `${from}-${to}`;
  if (from !== null) return `${from}+`;
  return `Through ${to}`;
}

/** Maps populated filter fields to their existing removal patches. */
function buildActiveChips(
  filters: ScreenerFilterState,
  onFilterPatch: (filters: Partial<ScreenerFilterState>) => void
): ActiveChip[] {
  const chips: ActiveChip[] = [];
  const add = (chip: ActiveChip) => chips.push(chip);

  if (filters.query) {
    add({
      key: "query",
      label: "Search",
      value: filters.query,
      removeLabel: "Remove Search filter",
      onRemove: () => onFilterPatch({ query: "" })
    });
  }
  if (filters.filingDateFrom !== null || filters.filingDateTo !== null) {
    add({
      key: "filing-date",
      label: "Filing date",
      value: dateRangeValue(filters.filingDateFrom, filters.filingDateTo),
      removeLabel: "Remove Filing date filter",
      onRemove: () => onFilterPatch({ filingDateFrom: null, filingDateTo: null })
    });
  }
  if (filters.tradeDateFrom !== null || filters.tradeDateTo !== null) {
    add({
      key: "trade-date",
      label: "Trade date",
      value: dateRangeValue(filters.tradeDateFrom, filters.tradeDateTo),
      removeLabel: "Remove Trade date filter",
      onRemove: () => onFilterPatch({ tradeDateFrom: null, tradeDateTo: null })
    });
  }
  if (filters.signalBuckets.length > 0) {
    add({
      key: "signals",
      label: "Signal",
      value: joined(filters.signalBuckets),
      removeLabel: "Remove Signal filter",
      onRemove: () => onFilterPatch({ signalBuckets: [] })
    });
  }
  if (filters.relationshipFlags.length > 0) {
    add({
      key: "relationships",
      label: "Relationship",
      value: joined(filters.relationshipFlags),
      removeLabel: "Remove Relationship filter",
      onRemove: () => onFilterPatch({ relationshipFlags: [] })
    });
  }
  if (filters.minTransactionValue !== null || filters.maxTransactionValue !== null) {
    add({
      key: "transaction-value",
      label: "Value",
      value: rangeValue(filters.minTransactionValue, filters.maxTransactionValue, formatCurrency),
      removeLabel: "Remove Transaction value filter",
      onRemove: () => onFilterPatch({ minTransactionValue: null, maxTransactionValue: null })
    });
  }
  if (filters.planStatuses.length > 0) {
    add({
      key: "plans",
      label: "Plan",
      value: joined(filters.planStatuses),
      removeLabel: "Remove Plan filter",
      onRemove: () => onFilterPatch({ planStatuses: [] })
    });
  }
  if (filters.minDistinctInsiders !== null) {
    add({
      key: "cluster",
      label: "Cluster",
      value: `${formatNumber(filters.minDistinctInsiders)}+ insiders`,
      removeLabel: "Remove Cluster filter",
      onRemove: () => onFilterPatch({ minDistinctInsiders: null })
    });
  }
  if (filters.exchanges.length > 0) {
    add({
      key: "exchanges",
      label: "Exchange",
      value: joined(filters.exchanges.map(formatExchangeLabel)),
      removeLabel: "Remove Exchange filter",
      onRemove: () => onFilterPatch({ exchanges: [] })
    });
  }
  if (filters.transactionActivities.length > 0) {
    add({
      key: "activities",
      label: "Activity",
      value: joined(filters.transactionActivities),
      removeLabel: "Remove Activity filter",
      onRemove: () => onFilterPatch({ transactionActivities: [] })
    });
  }
  if (filters.transactionCodes.length > 0) {
    add({
      key: "codes",
      label: "Code",
      value: joined(filters.transactionCodes),
      removeLabel: "Remove Transaction code filter",
      onRemove: () => onFilterPatch({ transactionCodes: [] })
    });
  }
  if (filters.securityStatuses.length > 0) {
    add({
      key: "securities",
      label: "Security",
      value: joined(filters.securityStatuses),
      removeLabel: "Remove Security status filter",
      onRemove: () => onFilterPatch({ securityStatuses: [] })
    });
  }
  if (filters.minShares !== null || filters.maxShares !== null) {
    add({
      key: "shares",
      label: "Shares",
      value: rangeValue(filters.minShares, filters.maxShares),
      removeLabel: "Remove Shares filter",
      onRemove: () => onFilterPatch({ minShares: null, maxShares: null })
    });
  }
  if (filters.minPrice !== null || filters.maxPrice !== null) {
    add({
      key: "price",
      label: "Price",
      value: rangeValue(filters.minPrice, filters.maxPrice, formatCurrency),
      removeLabel: "Remove Price filter",
      onRemove: () => onFilterPatch({ minPrice: null, maxPrice: null })
    });
  }
  if (filters.minHoldingsChange !== null || filters.maxHoldingsChange !== null) {
    add({
      key: "holdings",
      label: "Holdings change",
      value: rangeValue(filters.minHoldingsChange, filters.maxHoldingsChange),
      removeLabel: "Remove Holdings change filter",
      onRemove: () => onFilterPatch({ minHoldingsChange: null, maxHoldingsChange: null })
    });
  }
  if (filters.confidences.length > 0) {
    add({
      key: "confidence",
      label: "Confidence",
      value: joined(filters.confidences),
      removeLabel: "Remove Confidence filter",
      onRemove: () => onFilterPatch({ confidences: [] })
    });
  }
  if (filters.amendmentStates.length > 0) {
    add({
      key: "amendments",
      label: "Amendment",
      value: joined(filters.amendmentStates),
      removeLabel: "Remove Amendment filter",
      onRemove: () => onFilterPatch({ amendmentStates: [] })
    });
  }
  if (filters.valueQualities.length > 0) {
    add({
      key: "quality",
      label: "Value quality",
      value: joined(filters.valueQualities),
      removeLabel: "Remove Value quality filter",
      onRemove: () => onFilterPatch({ valueQualities: [] })
    });
  }
  if (filters.missingFieldFlags.length > 0) {
    add({
      key: "missing",
      label: "Missing fields",
      value: joined(filters.missingFieldFlags),
      removeLabel: "Remove Missing fields filter",
      onRemove: () => onFilterPatch({ missingFieldFlags: [] })
    });
  }
  if (filters.industryDescription) {
    add({
      key: "industry",
      label: "Industry",
      value: filters.industryDescription,
      removeLabel: "Remove Industry filter",
      onRemove: () => onFilterPatch({ industryDescription: "" })
    });
  }
  if (filters.minFilingLagDays !== null || filters.maxFilingLagDays !== null) {
    add({
      key: "filing-lag",
      label: "Filing lag",
      value: rangeValue(filters.minFilingLagDays, filters.maxFilingLagDays),
      removeLabel: "Remove Filing lag filter",
      onRemove: () => onFilterPatch({ minFilingLagDays: null, maxFilingLagDays: null })
    });
  }
  if (filters.accessionNumber) {
    add({
      key: "accession",
      label: "Accession",
      value: filters.accessionNumber,
      removeLabel: "Remove Accession filter",
      onRemove: () => onFilterPatch({ accessionNumber: "" })
    });
  }
  if (filters.favoritesOnly) {
    add({
      key: "favorites",
      label: "Watchlist",
      value: "Favorites only",
      removeLabel: "Remove Watchlist filter",
      onRemove: () => onFilterPatch({ favoritesOnly: false })
    });
  }

  return chips;
}

/** Keeps every active filter visible and directly removable. */
export function ActiveFilterChips({ filters, onFilterPatch }: ActiveFilterChipsProps) {
  const chips = buildActiveChips(filters, onFilterPatch);
  if (chips.length === 0) return null;

  return (
    <div className="flex min-h-9 items-center gap-1 overflow-x-auto border-t border-border px-2 py-1">
      {chips.map((chip) => (
        <FilterChip
          key={chip.key}
          label={chip.label}
          value={chip.value}
          removeLabel={chip.removeLabel}
          onRemove={chip.onRemove}
        />
      ))}
    </div>
  );
}
